/**
 * Profilkarte (Banner, animierter Avatar, Status, Namen, Pronomen, Bio). Genutzt im Popout und als
 * Live-Vorschau in den Einstellungen. Profil-Themefarben ergeben den Hintergrund-Verlauf.
 */
import { activeCustomStatus } from './customStatus';
import { CustomStatusText } from './CustomStatusText';
import type { CustomStatusDto, PresenceStatus, UserDto } from '@lagerfeuer/shared';
import type { CSSProperties, ReactNode } from 'react';
import { Avatar, STATUS_LABELS } from '../../components/Avatar';
import { MarkdownInline } from '../messages/Markdown';
import './profile.css';

export interface ProfileCardUser
  extends Pick<UserDto, 'id' | 'username' | 'displayName' | 'avatarUrl' | 'avatarAnimated' | 'bannerUrl' | 'bannerColor' | 'bio' | 'pronouns' | 'themePrimary' | 'themeAccent'> {
  isBot?: boolean;
}

interface Props {
  user: ProfileCardUser;
  /** Spitzname/Server-Avatar/-Banner/-Bio überschreiben die globalen Werte. */
  nickname?: string | null;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  bio?: string | null;
  /** null = keinen Status anzeigen. */
  status?: PresenceStatus | null;
  customStatus?: CustomStatusDto | null;
  serverId?: string | null;
  children?: ReactNode;
  actions?: ReactNode;
  testId?: string;
}

const HEX = /^#[0-9a-f]{6}$/i;

export function profileThemeStyle(primary: string | null | undefined, accent: string | null | undefined): CSSProperties | undefined {
  const p = primary && HEX.test(primary) ? primary : null;
  const a = accent && HEX.test(accent) ? accent : null;
  if (!p && !a) return undefined;
  return { '--lf-profile-primary': p ?? a, '--lf-profile-accent': a ?? p } as CSSProperties;
}

export function ProfileCard({ user, nickname, avatarUrl, bannerUrl, bio, status, customStatus, serverId, children, actions, testId = 'profile-card' }: Props) {
  const themed = profileThemeStyle(user.themePrimary, user.themeAccent);
  const banner = bannerUrl ?? user.bannerUrl;
  const bannerColor = user.bannerColor && HEX.test(user.bannerColor) ? user.bannerColor : null;
  const shownBio = bio ?? user.bio;
  const name = nickname || user.displayName || user.username;
  const cs = activeCustomStatus(customStatus);
  return (
    <div className="lf-profile-card" data-themed={themed ? 'true' : undefined} style={themed} data-testid={testId}>
      <div
        className="lf-profile-banner"
        style={banner ? undefined : bannerColor ? { background: bannerColor } : undefined}
        data-testid="profile-banner"
        data-has-image={banner ? 'true' : undefined}
      >
        {banner && <img src={banner} alt="" />}
      </div>
      <div className="lf-profile-avatar-wrap">
        <span className="lf-profile-avatar">
          <Avatar user={{ ...user, avatarUrl: avatarUrl ?? user.avatarUrl }} size={84} animate />
          {status && <span className="status-dot" data-status={status === 'INVISIBLE' ? 'OFFLINE' : status} role="img" aria-label={STATUS_LABELS[status]} data-testid="profile-status" />}
        </span>
        {actions && <div className="lf-profile-actions">{actions}</div>}
      </div>
      <div className="lf-profile-body">
        <div className="lf-profile-names">
          <div className="lf-profile-name" data-testid="profile-name">
            {name} {user.isBot && <span className="bot-tag">BOT</span>}
          </div>
          <div className="lf-profile-username small">
            <span data-testid="profile-username">{user.username}</span>
            {user.pronouns && (
              <>
                <span aria-hidden="true"> · </span>
                <span data-testid="profile-pronouns">{user.pronouns}</span>
              </>
            )}
          </div>
          {cs && (
            <div className="lf-profile-custom-status small" data-testid="profile-custom-status">
              <CustomStatusText status={cs} emojiSize={18} />
            </div>
          )}
        </div>
        {shownBio && (
          <div className="lf-profile-section">
            <div className="lf-profile-section-title">Über mich</div>
            <div className="lf-profile-bio" data-testid="profile-bio-view">
              <MarkdownInline content={shownBio} serverId={serverId} block />
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
